import { useId } from 'react'

export type ChartAnimation = 'inview' | 'autoplay' | 'manual'

export type Point = [number, number]

type Segment = {
    c1: Point
    c2: Point
    to: Point
}

const fixed = (value: number): string => (Math.round(value * 100) / 100).toString()

/** Stable, CSS-safe id for gradients and clip paths. */
export const useChartId = (prefix: string): string => {
    const id = useId()

    return `${prefix}-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`
}

/** Rounds a maximum up to a readable axis value (1, 2, 2.5 or 5 times a power of ten). */
export const niceCeil = (value: number): number => {
    if (value <= 0) return 1

    const exponent = Math.floor(Math.log10(value))
    const base = Math.pow(10, exponent)
    const fraction = value / base
    const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : fraction <= 6 ? 6 : fraction <= 8 ? 8 : 10

    return nice * base
}

/** Padded min and max across every series so lines never touch the edges. */
export const domainOf = (groups: Array<Array<number>>, padding = 0.12): [number, number] => {
    const values = groups.flat()

    if (!values.length) return [0, 1]

    const min = Math.min(...values)
    const max = Math.max(...values)
    const range = max - min || Math.abs(max) || 1

    return [min - range * padding, max + range * padding]
}

/** Maps values to 0-100 viewBox coordinates (y grows downward). */
export const toPoints = (values: Array<number>, domain: [number, number], count = values.length): Array<Point> => {
    const [min, max] = domain
    const span = max - min || 1

    return values.map((value, index) => [
        count === 1 ? 50 : (index / (count - 1)) * 100,
        100 - ((value - min) / span) * 100
    ])
}

/** Monotone cubic interpolation (no overshoot), returned as segments. */
const monotoneSegments = (points: Array<Point>): Array<Segment> => {
    const count = points.length

    if (count < 2) return []

    const dx: Array<number> = []
    const slopes: Array<number> = []

    for (let index = 0; index < count - 1; index++) {
        const width = points[index + 1][0] - points[index][0] || 1e-6

        dx.push(width)
        slopes.push((points[index + 1][1] - points[index][1]) / width)
    }

    const tangents: Array<number> = [slopes[0]]

    for (let index = 1; index < count - 1; index++) {
        const before = slopes[index - 1]
        const after = slopes[index]

        if (before * after <= 0) {
            tangents.push(0)
        }
        else {
            const common = dx[index - 1] + dx[index]

            tangents.push((3 * common) / ((common + dx[index]) / before + (common + dx[index - 1]) / after))
        }
    }

    tangents.push(slopes[count - 2])

    const segments: Array<Segment> = []

    for (let index = 0; index < count - 1; index++) {
        const [x0, y0] = points[index]
        const [x1, y1] = points[index + 1]
        const third = dx[index] / 3

        segments.push({
            c1: [x0 + third, y0 + tangents[index] * third],
            c2: [x1 - third, y1 - tangents[index + 1] * third],
            to: [x1, y1]
        })
    }

    return segments
}

const segmentsToPath = (start: Point, segments: Array<Segment>, move = true): string => {
    let path = move ? `M${fixed(start[0])},${fixed(start[1])}` : `L${fixed(start[0])},${fixed(start[1])}`

    for (const { c1, c2, to } of segments) {
        path += ` C${fixed(c1[0])},${fixed(c1[1])} ${fixed(c2[0])},${fixed(c2[1])} ${fixed(to[0])},${fixed(to[1])}`
    }

    return path
}

export const linePath = (points: Array<Point>): string => {
    if (!points.length) return ''

    return segmentsToPath(points[0], monotoneSegments(points))
}

export const areaPath = (points: Array<Point>, baseline = 100): string => {
    if (!points.length) return ''

    const first = points[0]
    const last = points[points.length - 1]

    return `${linePath(points)} L${fixed(last[0])},${baseline} L${fixed(first[0])},${baseline} Z`
}

/** Closed shape between an upper and a lower curve (forecast bands). */
export const bandPath = (upper: Array<Point>, lower: Array<Point>): string => {
    if (!upper.length || !lower.length) return ''

    const top = linePath(upper)
    const reversed = [...lower].reverse()
    const bottomSegments = monotoneSegments(reversed)

    return `${top} ${segmentsToPath(reversed[0], bottomSegments, false)} Z`
}

export const percentOf = (value: number, total: number): number => {
    if (!total) return 0

    return (value / total) * 100
}
